import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Helper to get full media URL
export const getMediaUrl = (path) => {
  if (!path) return null;
  // If already a full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // Fix http to https if needed
    return path.replace('http://', 'https://');
  }
  // If relative path, prepend backend URL
  return `${BACKEND_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Create axios instance with credentials
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Projects API
export const projectsAPI = {
  create: (data) => api.post('/projects', data),
  list: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
  update: (id, data) => api.patch(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  updateSettings: (id, settings) => api.patch(`/projects/${id}/settings`, settings),
  
  // Clip management (multiple videos)
  uploadClip: (id, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${id}/upload-clip`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  updateClip: (projectId, clipId, data) => api.patch(`/projects/${projectId}/clips/${clipId}`, data),
  deleteClip: (projectId, clipId) => api.delete(`/projects/${projectId}/clips/${clipId}`),
  reorderClips: (projectId, clipIds) => api.put(`/projects/${projectId}/clips/reorder`, clipIds),
  
  // Legacy single video (uses clip system now)
  uploadVideo: (id, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${id}/upload-clip`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  uploadAudio: (id, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${id}/upload-audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  startProcessing: (id) => api.post(`/projects/${id}/process`),
  getStatus: (id) => api.get(`/projects/${id}/status`),
  generateCaptions: (id) => api.post(`/projects/${id}/generate-captions`),
};

// Subscription API
export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  createCheckout: (planId, originUrl) => api.post('/subscription/checkout', { plan_id: planId, origin_url: originUrl }),
  getCheckoutStatus: (sessionId) => api.get(`/subscription/status/${sessionId}`),
};

// Templates API
export const templatesAPI = {
  getCaptionStyles: () => api.get('/templates/caption-styles'),
  getVideoTypes: () => api.get('/templates/video-types'),
  getTransitions: () => api.get('/templates/transitions'),
};

// Announcements API
export const announcementsAPI = {
  getActive: () => api.get('/announcements'),
};

// Admin API
export const adminAPI = {
  // Users
  getUsers: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // Projects
  getProjects: () => api.get('/admin/projects'),
  getProject: (id) => api.get(`/admin/projects/${id}`),
  deleteProject: (id) => api.delete(`/admin/projects/${id}`),
  
  // Payments
  getPayments: () => api.get('/admin/payments'),
  
  // Content Management - Caption Styles
  getCaptionStyles: () => api.get('/admin/templates/caption-styles'),
  createCaptionStyle: (data) => api.post('/admin/templates/caption-styles', data),
  updateCaptionStyle: (id, data) => api.put(`/admin/templates/caption-styles/${id}`, data),
  deleteCaptionStyle: (id) => api.delete(`/admin/templates/caption-styles/${id}`),
  
  // Content Management - Video Types
  getVideoTypes: () => api.get('/admin/templates/video-types'),
  createVideoType: (data) => api.post('/admin/templates/video-types', data),
  updateVideoType: (id, data) => api.put(`/admin/templates/video-types/${id}`, data),
  deleteVideoType: (id) => api.delete(`/admin/templates/video-types/${id}`),
  
  // Content Management - Transitions
  getTransitions: () => api.get('/admin/templates/transitions'),
  createTransition: (data) => api.post('/admin/templates/transitions', data),
  deleteTransition: (id) => api.delete(`/admin/templates/transitions/${id}`),
  
  // Pricing Management
  getPricing: () => api.get('/admin/pricing'),
  updatePricing: (planId, data) => api.put(`/admin/pricing/${planId}`, data),
  
  // Announcements
  getAnnouncements: () => api.get('/admin/announcements'),
  createAnnouncement: (data) => api.post('/admin/announcements', data),
  deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),
  toggleAnnouncement: (id) => api.patch(`/admin/announcements/${id}/toggle`),
};

export default api;
